import useCookies from "react-use-cookie";
import { constants, storage } from "../utils";

// Owns the ACTIVE CUSTOMER's persistence — the 60-day `Customer` cookie plus its localStorage twin.
//
// This lived inside CustomerDashboard.js, which split the source of truth: the value itself sat in
// App.js (`customerFilter`) while the reading and writing of it sat in the dashboard. Nothing outside
// that one screen could restore or persist the active account, so an app-wide account picker was
// impossible. Lifting it here makes App.js the single owner and every screen a consumer.
//
// Cookie AND localStorage are both written, as before: the cookie carries the 60-day lifetime, the
// localStorage copy is the fallback when cookies are unavailable. Reads try the cookie first.
// This hook is deliberately a dumb store: it reads and writes what it is given and applies NO policy
// about which account a user may use. Deciding that is the caller's job (see resolveCustomerAccount in
// CustomerDashboard) — a reader that silently rewrote the stored value to the primary would make
// membership validation meaningless, because the candidate would always already be the primary.
export default function useActiveCustomer(currentUser, impersonating) {
   const [customerCookie, setCustomerCookie] = useCookies(constants.LocalStorageKeys.Customer, null);

   function write(data) {
      try {
         storage.setItem(constants.LocalStorageKeys.Customer, data);
         setCustomerCookie(data, {
            domain: window.location.hostname,
            sameSite: "Strict",
            days: 60,
         });
      } catch {
         console.error("Failed to save customer cookie");
      }
   }

   // The raw cookie value, for callers that only need to know whether anything was stored.
   const hasStoredCustomer = customerCookie && customerCookie !== "null";

   function readFromCookie() {
      if (!hasStoredCustomer) return null;
      try {
         return JSON.parse(customerCookie);
      } catch {
         return null;
      }
   }

   function readFromStorageFallback() {
      try {
         const fallback = storage.getItem(constants.LocalStorageKeys.Customer);
         if (!fallback || fallback === "null") return null;
         return JSON.parse(fallback);
      } catch {
         return null;
      }
   }

   // Persist the supplied account when it differs from what is already stored. A null/empty customer
   // is a NO-OP - it must never write anything, and specifically must never fall back to writing the
   // user's primary. That fallback (removed here) was a clobber: CustomerDashboard's persist effect
   // fires persist(customer) on mount while `customer` is still momentarily null, so on a reload it
   // overwrote a just-switched account (e.g. PARK11) with the primary (PARK05). A single-load browser
   // corrects it on the next effect; a debug session that loads the page twice can read the clobbered
   // value between the two loads and keep it. Defaulting to the primary is now owned where it belongs:
   // login (App.js signIn / 7C persists the primary explicitly) and the App.js customerFilter
   // initializer (falls back to the primary when nothing is stored).
   function persist(customer) {
      // Never persist while impersonating - it would overwrite the admin's own saved account with
      // the impersonated customer's.
      if (impersonating) return;
      if (!customer) return;
      const stored = readFromCookie();
      if (customer.value !== stored?.value) {
         write(JSON.stringify(Object.assign({}, customer)));
      }
   }

   function clear() {
      write("null");
   }

   return { hasStoredCustomer, readFromCookie, readFromStorageFallback, persist, clear };
}
